import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, ArrowDown } from "lucide-react";
import { SITE, IMAGES, scrollToId } from "@/lib/site";

const LINES = [
  { text: "Authentic Mexican,", cls: "text-cream" },
  { text: "kissed by fire,", cls: "text-terracotta italic" },
  { text: "made by family.", cls: "text-cream" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="A table full of authentic Mexican dishes at Alejandra's"
          className="w-full h-[120%] object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-obsidian/60" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to top, #0E0C0A 4%, rgba(14,12,10,0.25) 45%, rgba(14,12,10,0.45) 100%)" }}
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="flex flex-wrap items-center gap-3 mb-8"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-terracotta/40 bg-obsidian/50 backdrop-blur-md font-mono-d text-[11px] uppercase tracking-[0.2em] text-amber-warm"
            data-testid="hero-rating-badge"
          >
            <Star className="w-3.5 h-3.5 fill-amber-warm text-amber-warm" />
            {SITE.rating} · {SITE.reviews} reviews
          </span>
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cream/15 bg-obsidian/50 backdrop-blur-md font-mono-d text-[11px] uppercase tracking-[0.2em] text-cream/70"
            data-testid="hero-location-badge"
          >
            <MapPin className="w-3.5 h-3.5" /> Aurora, Indiana
          </span>
        </motion.div>

        <h1 className="font-serif-d font-semibold tracking-tight leading-[1.02] text-[13vw] sm:text-7xl lg:text-8xl" data-testid="hero-title">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg font-light text-cream/75 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Three generations of family recipes — hand-pressed tortillas, slow-simmered moles,
          sizzling fajitas and the coldest margaritas in Aurora.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("menu")}
            className="group px-8 py-4 rounded-full bg-terracotta text-obsidian font-semibold text-sm tracking-wide hover:bg-terracotta-hover transition-colors duration-300"
            data-testid="hero-explore-menu-btn"
          >
            Explore the Menu
          </button>
          <button
            onClick={() => scrollToId("reserve")}
            className="px-8 py-4 rounded-full border border-cream/30 text-cream font-medium text-sm tracking-wide hover:border-terracotta hover:text-terracotta transition-colors duration-300 backdrop-blur-sm"
            data-testid="hero-reserve-btn"
          >
            Reserve a Table
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 text-cream/50"
      >
        <span className="font-mono-d text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-[-8px]">Scroll</span>
        <ArrowDown className="w-4 h-4 mt-6 animate-bounce" />
      </motion.div>
    </section>
  );
}
